import { ALL_FORMATS, Input, UrlSource } from "mediabunny";
import type {
    ScriptLayerInputConfig,
    SpeakerOverride,
} from "../../../engine/clips/script/script.interfaces";
import { CompositionService } from "../../../librettoEditor/services/CompositionService";
import { CompositionStateStore } from "../../../librettoEditor/store/CompositionStateStore";
import { ScriptLayerStore } from "../../../librettoEditor/store/ScriptLayerStore";
import { SequencePersistenceService } from "../../../sequenceEditor/services/SequencePersistenceService";
import { ProjectAssetsStore } from "../../../state/ProjectAssetsStore";
import { maybeLockCanvasToFirstVideo } from "../../../utils/canvasSizing";
import { CreateScriptClipForComposition } from "../utils";
import {
    calculateVideoVisualDimensions,
    parseVideoResolution,
    resolveVideoDimensionsFromInput,
} from "./videoSizing";

type ResolveTrackUrlFn = (
    trackId: string,
    type: "audio" | "video",
) => Promise<string | undefined>;

type CreateSpeakerFn = (args: {
    projectId: string;
    name: string;
}) => Promise<string | null | undefined>;

type InsertSequenceIntoScriptLayerArgs = {
    sequenceId: string;
    userId: string;
    workspaceId?: string | null;
    resolveTrackUrl: ResolveTrackUrlFn;
    createSpeaker?: CreateSpeakerFn;
};

const DEFAULT_SPEAKER_COLORS = [
    "#3B82F6",
    "#EF4444",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
];

export async function insertSequenceIntoScriptLayer({
    sequenceId,
    userId,
    workspaceId,
    resolveTrackUrl,
    createSpeaker,
}: InsertSequenceIntoScriptLayerArgs): Promise<any | null> {
    const normalizedSequenceId = String(sequenceId ?? "").trim();
    const normalizedUserId = String(userId ?? "").trim();
    if (!normalizedSequenceId || !normalizedUserId) {
        return null;
    }

    SequencePersistenceService.init(
        normalizedUserId,
        normalizedSequenceId,
        workspaceId ?? undefined,
    );

    try {
        const { layers: layersWithClips } =
            await SequencePersistenceService.fetchClipsByLayerOrdered(normalizedSequenceId);

        const composition = CompositionService.composition;
        if (!composition) {
            return null;
        }

        const allClips = layersWithClips.flatMap((layerData: any) =>
            Array.isArray(layerData?.clips) ? layerData.clips : [],
        );
        try {
            const firstVideoRec = allClips.find(
                (record: any) => String(record?.type ?? "").toLowerCase() === "video",
            );
            if (firstVideoRec) {
                let videoWidth = Number(firstVideoRec?.props?.width ?? 0);
                let videoHeight = Number(firstVideoRec?.props?.height ?? 0);
                if (!(videoWidth > 0 && videoHeight > 0)) {
                    const trackRecord = ProjectAssetsStore.projectTracks.find(
                        (track: any) =>
                            track?.id === firstVideoRec.assetId ||
                            track?.trackId === firstVideoRec.assetId,
                    );
                    const parsedResolution = parseVideoResolution(
                        String(trackRecord?.resolution ?? ""),
                    );
                    videoWidth = parsedResolution.width;
                    videoHeight = parsedResolution.height;
                }
                if (!(videoWidth > 0 && videoHeight > 0)) {
                    const firstVideoTrackId = String(firstVideoRec?.assetId ?? "").trim();
                    if (firstVideoTrackId) {
                        const firstVideoUrl = await resolveTrackUrl(firstVideoTrackId, "video");
                        if (firstVideoUrl) {
                            const firstVideoInput = new Input({
                                source: new UrlSource(firstVideoUrl),
                                formats: ALL_FORMATS,
                            });
                            const resolvedDims = await resolveVideoDimensionsFromInput(firstVideoInput);
                            videoWidth = resolvedDims.width;
                            videoHeight = resolvedDims.height;
                        }
                    }
                }
                if (videoWidth > 0 && videoHeight > 0) {
                    maybeLockCanvasToFirstVideo(composition, videoWidth, videoHeight);
                }
            }
        } catch {}

        const compWidth = composition?.width ?? 1920;
        const compHeight = composition?.height ?? 1080;
        const inputConfigs: ScriptLayerInputConfig[] = [];
        const genSegId = () =>
            crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;

        for (const layerData of layersWithClips) {
            const layerClips: any[] = Array.isArray(layerData?.clips)
                ? layerData.clips
                : [];
            if (layerClips.length === 0) continue;

            let layerName = String(layerData?.name ?? "").trim();
            if (!layerName) {
                const firstClip = layerClips.find((c: any) => c?.assetId) ?? layerClips[0];
                layerName = String(firstClip?.name ?? "").trim();
                if (!layerName) {
                    const firstAssetId = firstClip?.assetId;
                    const trackRecord = ProjectAssetsStore.projectTracks.find(
                        (track: any) =>
                            track?.id === firstAssetId || track?.trackId === firstAssetId,
                    );
                    layerName = String(
                        trackRecord?.title || trackRecord?.name || "",
                    ).trim();
                }
            }

            const clipsByKey = new Map<string, any[]>();
            for (const rec of layerClips) {
                const assetId = rec?.assetId;
                if (!assetId) continue;
                const type = String(rec?.type ?? "audio").toLowerCase();
                const key = `${assetId}::${type}`;
                const existing = clipsByKey.get(key) ?? [];
                existing.push(rec);
                clipsByKey.set(key, existing);
            }

            for (const [key, recs] of clipsByKey.entries()) {
                try {
                    const firstRec: any = recs[0];
                    const assetId = String(firstRec?.assetId ?? "").trim();
                    if (!assetId) continue;

                    const type = String(firstRec?.type ?? "audio").toLowerCase();
                    const isVideo = type === "video";
                    const kind: "audio" | "video" = isVideo ? "video" : "audio";

                    const url = await resolveTrackUrl(assetId, kind);
                    if (!url) continue;

                    const input = new Input({
                        source: new UrlSource(url),
                        formats: ALL_FORMATS,
                    });

                    const sourceColor =
                        firstRec?.waveformColor ||
                        DEFAULT_SPEAKER_COLORS[
                            inputConfigs.length % DEFAULT_SPEAKER_COLORS.length
                        ];

                    const segments = recs
                        .map((rec: any) => {
                            const offsetSec = Number(rec?.startSec ?? 0);
                            const inSec = Number(rec?.inSec ?? 0);
                            const outSec = Number(rec?.outSec ?? 0);
                            if (!Number.isFinite(offsetSec) || offsetSec < 0) return null;
                            if (
                                !Number.isFinite(inSec) ||
                                !Number.isFinite(outSec) ||
                                outSec <= inSec
                            ) {
                                return null;
                            }

                            const seg: any = {
                                id: genSegId(),
                                offsetSec,
                                range: { inSec, outSec },
                            };
                            const segRate = Number(rec?.props?.playbackRate ?? 1.0);
                            if (
                                Number.isFinite(segRate) &&
                                segRate > 0 &&
                                segRate !== 1.0
                            ) {
                                seg.playbackRate = segRate;
                            }
                            return seg;
                        })
                        .filter(Boolean)
                        .sort(
                            (a: any, b: any) =>
                                Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0),
                        );

                    if (segments.length === 0) continue;

                    const config: ScriptLayerInputConfig = {
                        input,
                        assetId,
                        name: layerName,
                        color: sourceColor,
                        gain:
                            firstRec?.props?.gain !== undefined
                                ? Number(firstRec.props.gain)
                                : 1.0,
                        segments,
                    };

                    if (isVideo) {
                        let videoWidth = firstRec?.props?.width ?? 0;
                        let videoHeight = firstRec?.props?.height ?? 0;

                        if (!videoWidth || !videoHeight) {
                            const trackRecord = ProjectAssetsStore.projectTracks.find(
                                (track: any) =>
                                    track?.id === assetId || track?.trackId === assetId,
                            );
                            const parsedResolution = parseVideoResolution(
                                String(trackRecord?.resolution ?? ""),
                            );
                            videoWidth = parsedResolution.width;
                            videoHeight = parsedResolution.height;
                        }
                        if (!(videoWidth > 0 && videoHeight > 0)) {
                            const resolvedDims = await resolveVideoDimensionsFromInput(input);
                            videoWidth = resolvedDims.width;
                            videoHeight = resolvedDims.height;
                        }

                        const visualDims = calculateVideoVisualDimensions(
                            videoWidth,
                            videoHeight,
                            compWidth,
                            compHeight,
                        );
                        config.visual = {
                            x: visualDims.x,
                            y: visualDims.y,
                            width: visualDims.width,
                            height: visualDims.height,
                            alpha: 1.0,
                            ...(videoWidth > 0 && videoHeight > 0
                                ? {
                                      originalWidth: videoWidth,
                                      originalHeight: videoHeight,
                                  }
                                : {}),
                        } as any;
                    }

                    inputConfigs.push(config);
                } catch (error) {
                    console.error(
                        "[insertSequenceIntoScriptLayer] Failed to create input config from sequence layer",
                        { key },
                        error,
                    );
                }
            }
        }

        if (inputConfigs.length === 0) {
            return null;
        }

        try {
            const existingScript: any = CompositionService.getScriptLayerClip?.();
            if (existingScript && String(existingScript.type) === "script") {
                const computeSeqDurationSec = (cfgs: ScriptLayerInputConfig[]): number => {
                    let maxEnd = 0;
                    for (const cfg of cfgs) {
                        const cfgRate = Number((cfg as any)?.playbackRate ?? 1.0) || 1.0;
                        const segs: any[] = Array.isArray((cfg as any)?.segments)
                            ? ((cfg as any).segments as any[])
                            : [];
                        for (const seg of segs) {
                            const off = Number(seg?.offsetSec ?? 0);
                            const pr = Number(seg?.playbackRate ?? cfgRate) || 1.0;
                            const rin = Number(seg?.range?.inSec ?? NaN);
                            const rout = Number(seg?.range?.outSec ?? NaN);
                            if (!Number.isFinite(off) || off < 0) continue;
                            if (!Number.isFinite(pr) || pr <= 0) continue;
                            if (!Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) {
                                continue;
                            }
                            const dur = (rout - rin) / pr;
                            maxEnd = Math.max(maxEnd, off + dur);
                        }
                    }
                    return maxEnd;
                };

                const seqDurSec = computeSeqDurationSec(inputConfigs);
                if (!Number.isFinite(seqDurSec) || seqDurSec <= 1e-3) {
                    return null;
                }

                const absCompTime = Number(
                    CompositionStateStore.currentTime ??
                        CompositionService.getCurrentTime() ??
                        0,
                );
                let atClipLocalSec: number | null = null;
                try {
                    if (typeof existingScript.mapCompTimeToClipLocal === "function") {
                        atClipLocalSec = existingScript.mapCompTimeToClipLocal(absCompTime);
                    }
                } catch {}
                if (atClipLocalSec == null) {
                    try {
                        atClipLocalSec =
                            ScriptLayerStore.mapTimelineTimeToSourceTime(absCompTime);
                    } catch {}
                }
                if (atClipLocalSec == null || !Number.isFinite(atClipLocalSec)) {
                    const inSec = Number(existingScript.range?.[0]?.seconds ?? 0);
                    const outSec = Number(
                        existingScript.range?.[1]?.seconds ??
                            existingScript.duration?.seconds ??
                            inSec,
                    );
                    atClipLocalSec = Number.isFinite(outSec) ? outSec : inSec;
                }

                const ok = CompositionService.insertEmptyScriptScene(
                    Number(atClipLocalSec),
                    Number(seqDurSec),
                );
                if (!ok) {
                    return null;
                }

                const existingCfgs: any[] =
                    typeof existingScript.getInputConfigs === "function"
                        ? (existingScript.getInputConfigs() as any[])
                        : [];
                const hasAsset = (assetId: string): boolean =>
                    existingCfgs.some(
                        (cfg: any) =>
                            String(cfg?.assetId ?? "") === String(assetId ?? ""),
                    );

                const shiftedSegmentsFor = (cfg: ScriptLayerInputConfig): any[] => {
                    const segs: any[] = Array.isArray((cfg as any)?.segments)
                        ? ((cfg as any).segments as any[])
                        : [];
                    return segs
                        .map((seg: any) => {
                            const off = Number(seg?.offsetSec ?? 0);
                            const rin = Number(seg?.range?.inSec ?? NaN);
                            const rout = Number(seg?.range?.outSec ?? NaN);
                            if (!Number.isFinite(off) || off < 0) return null;
                            if (!Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) {
                                return null;
                            }
                            const rec: any = {
                                id: seg?.id ? String(seg.id) : genSegId(),
                                offsetSec: off + Number(atClipLocalSec),
                                range: { inSec: rin, outSec: rout },
                            };
                            const pr = seg?.playbackRate;
                            if (Number.isFinite(pr) && Number(pr) > 0) {
                                rec.playbackRate = Number(pr);
                            }
                            return rec;
                        })
                        .filter(Boolean)
                        .sort(
                            (a: any, b: any) =>
                                Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0),
                        );
                };

                for (const cfg of inputConfigs) {
                    const assetId = String((cfg as any)?.assetId ?? "").trim();
                    if (!assetId) continue;
                    const segs = shiftedSegmentsFor(cfg);
                    if (segs.length === 0) continue;

                    if (hasAsset(assetId)) {
                        try {
                            existingScript.appendSourceSegments?.(assetId, segs);
                        } catch {}
                        continue;
                    }

                    try {
                        (cfg as any).segments = segs;
                        await existingScript.appendInputConfig?.(cfg);
                    } catch (error) {
                        console.warn(
                            "[insertSequenceIntoScriptLayer] Failed to append new script source",
                            { assetId },
                            error,
                        );
                    }
                }

                try {
                    await CompositionService.composition.onClipTimingChanged();
                } catch {}
                try {
                    CompositionService.notifyScriptLayerUpdated({}, { timingChanged: false });
                } catch {}
                try {
                    CompositionService.composition.computeFrame();
                } catch {}

                return existingScript;
            }
        } catch {}

        const projectId = String(ProjectAssetsStore.projectId ?? "").trim();
        const speakerIdBySourceIndex = new Map<number, string>();
        if (projectId && createSpeaker) {
            for (let sourceIndex = 0; sourceIndex < inputConfigs.length; sourceIndex += 1) {
                const config = inputConfigs[sourceIndex];
                const name = config.name?.trim() ?? "";
                if (!name) continue;

                const existingSpeaker = ProjectAssetsStore.projectSpeakers.find(
                    (speaker: any) =>
                        String(speaker.name ?? "").toLowerCase() === name.toLowerCase(),
                );
                if (existingSpeaker) {
                    const speakerId = String(
                        existingSpeaker.speakerId ?? existingSpeaker.id ?? "",
                    );
                    if (speakerId) speakerIdBySourceIndex.set(sourceIndex, speakerId);
                    continue;
                }

                try {
                    const speakerId = await createSpeaker({ projectId, name });
                    if (speakerId) {
                        speakerIdBySourceIndex.set(sourceIndex, speakerId);
                    }
                } catch (error) {
                    console.warn(
                        "[insertSequenceIntoScriptLayer] Failed to create speaker",
                        name,
                        error,
                    );
                }
            }
        }

        const sequenceName =
            ProjectAssetsStore.projectSequences.find(
                (sequence: any) => sequence.id === normalizedSequenceId,
            )?.title || "Sequence";

        const scriptLayerClip = await CreateScriptClipForComposition({
            inputConfigs,
            composition,
            stateStore: CompositionStateStore,
            clipName: sequenceName,
        });
        if (!scriptLayerClip) {
            return null;
        }

        try {
            const endSec = Number(
                scriptLayerClip.duration?.seconds ??
                    scriptLayerClip.range?.[1]?.seconds ??
                    0,
            );
            if (Number.isFinite(endSec) && endSec > 0) {
                const overrides: SpeakerOverride[] = [];
                for (let sourceIndex = 0; sourceIndex < inputConfigs.length; sourceIndex += 1) {
                    const config = inputConfigs[sourceIndex];
                    const name = config.name?.trim() ?? "";
                    if (!name) continue;

                    const speakerId = speakerIdBySourceIndex.get(sourceIndex);
                    overrides.push({
                        id:
                            crypto?.randomUUID?.() ??
                            `spk_${Date.now()}_${Math.random()}`,
                        startSec: 0,
                        endSec,
                        sourceIndex,
                        speakerName: name,
                        color:
                            typeof config.color === "string"
                                ? config.color
                                : undefined,
                        speakerId,
                    });
                }
                if (overrides.length > 0) {
                    scriptLayerClip.applyPersistedEdits({
                        speakerOverrides: overrides,
                    });
                }
            }
        } catch {}

        const addedScriptClip = await CompositionService.addToScriptLayer(scriptLayerClip);
        return addedScriptClip ?? scriptLayerClip;
    } finally {
        SequencePersistenceService.reset();
    }
}
